import classNames from "classnames/dedupe";
import { useMemo } from "react";
import {
  useCommercialLandlordRedirect,
  useQuestionnaireInitialization,
  useSections,
} from "../../../features/questionnaire";
import { useEnvironmentValues } from "../../../hooks/environment/useEnvironment";
import type { WithMetadata } from "../../../pages";
import { Section, type FlattenedSectionProps } from "../Section";
import { QuestionnaireForm } from "../QuestionnaireForm";
import { useLocale } from "../../../hooks/useLocale";
import { useSetSeeMoreCoversState } from "../../../hooks/useCoverGeniusSeeMoreCoversState";
import type { QuestionnaireProps } from "..";
import { Preamble, Postamble } from "../Amble";

/**
 * Stacked sections questionnaire variant.
 * Renders all sections at once in a single page without section navigation.
 */
export const StackedSectionsQuestionnaire = ({
  ref,
  ...props
}: WithMetadata<QuestionnaireProps>) => {
  const {
    elementType: Element = "div",
    config,
    sections,
    validations,
    componentName,
    actions,
    pageId,
    variant,
    preamble,
    preambleName,
    postamble,
    postambleName,
    ...otherProps
  } = props;
  const { locale } = useLocale();
  const { site } = useEnvironmentValues();

  // Include locale and variant in config object
  const configWithLocale = useMemo(
    () => ({
      ...config,
      locale,
      variant,
    }),
    [config, locale, variant],
  );
  useSetSeeMoreCoversState(false);
  useQuestionnaireInitialization(sections, validations, configWithLocale);
  useCommercialLandlordRedirect();

  // Get flattened sections from state (after initialization)
  // Note: Sections are flattened during initialization, but the type system doesn't know it
  const flattenedSections = useSections() as FlattenedSectionProps[];

  const submitActionLabel = useMemo(() => {
    const submitAction = actions?.find(
      action => action.actionType === "submit",
    );
    return submitAction?.label || "Continue to quotes";
  }, [actions]);

  const backAction = useMemo(() => {
    const foundBackAction = actions?.find(
      action => action.actionType === "back",
    );
    return foundBackAction;
  }, [actions]);

  const classes = classNames(
    "mobius",
    "mobius-questionnaire",
    "mobius-questionnaire--stacked-sections",
    otherProps.className,
  );

  // Wait for sections to be initialized to prevent layout shift where Actions show before questions
  const areSectionsInitialized = flattenedSections.length > 0;

  return (
    <Element
      ref={ref}
      {...otherProps}
      {...(componentName ? { "data-component": componentName } : {})}
      {...(variant ? { "data-variant": variant } : {})}
      className={classes}
    >
      <Preamble
        amble={preamble}
        ambleName={preambleName}
        site={site}
        locale={locale}
      />
      {areSectionsInitialized && (
        <QuestionnaireForm
          submitActionLabel={submitActionLabel}
          backAction={backAction}
          sections={sections}
          questionnaireVariant={variant}
        >
          {flattenedSections.map((section, index) => (
            <Section
              key={section.id || index}
              {...section}
              submitActionLabel={submitActionLabel}
              className={classNames(
                section.className,
                "mobius-section--stacked",
              )}
              variant={variant}
            />
          ))}
          <Postamble
            amble={postamble}
            ambleName={postambleName}
            site={site}
            locale={locale}
          />
        </QuestionnaireForm>
      )}
    </Element>
  );
};

StackedSectionsQuestionnaire.displayName = "StackedSectionsQuestionnaire";
